import { createSharedComposable } from '@vueuse/core'

/**
 * One instance per Nuxt app rather than one per process.
 *
 * `createSharedComposable` memoises on the module, which is a singleton in the
 * browser and a singleton across every request the server handles. That is fine
 * for a browser tab and wrong on a server: the first request to call the
 * composable pins its own state, and every later request in that process reads
 * it. With a page cached per group, one visitor's state would then be baked
 * into the page the whole group is served.
 *
 * In the browser this is `createSharedComposable` unchanged. On the server each
 * request gets its own instance, keyed on the Nuxt app, which is created per
 * request and thrown away with it.
 */
export function createRequestScopedComposable<Args extends unknown[], Result>(
  composable: (...args: Args) => Result
): (...args: Args) => Result {
  const shared = createSharedComposable(composable)
  // Keyed on the Nuxt app so an instance cannot outlive the request it belongs
  // to, and so nothing here is serialised into the payload.
  const perApp = new WeakMap<object, Result>()

  return (...args: Args): Result => {
    if (import.meta.client) return shared(...args)

    const nuxtApp = useNuxtApp()
    const existing = perApp.get(nuxtApp)
    if (existing !== undefined) return existing

    const created = composable(...args)
    perApp.set(nuxtApp, created)
    return created
  }
}
